import React, { useState, useEffect } from 'react';
import { Check, MessageCircle, FileText, Plane, ClipboardCheck, DollarSign, ArrowRight, Sparkles } from 'lucide-react';

export const HowItWorks = () => {
  const [howItWorks, setHowItWorks] = useState([]);
  const [loading, setLoading] = useState(true);

  // Icon mapping for each step
  const stepIcons = [
    MessageCircle,   // Step 1: Consultation
    DollarSign,      // Step 2: Quotation (changed from FileText)
    FileText,        // Step 3: Meticulous Documentation (moved from Step 2)
    ClipboardCheck,  // Step 4: Pre-Travel Checklist
    Plane            // Step 5: Travel Day (changed from Luggage)
  ];

  // Color themes for each step - aligned with site color scheme
  const stepThemes = [
    { 
      gradient: 'from-teal-500 to-cyan-500',
      lightGradient: 'from-teal-500/10 to-cyan-500/10',
      border: 'border-teal-500/30',
      iconBg: 'bg-gradient-to-br from-teal-500 to-cyan-600',
      text: 'text-teal-600'
    },
    { 
      gradient: 'from-blue-500 to-indigo-500',
      lightGradient: 'from-blue-500/10 to-indigo-500/10',
      border: 'border-blue-500/30',
      iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600',
      text: 'text-blue-600'
    },
    { 
      gradient: 'from-cyan-500 to-blue-500',
      lightGradient: 'from-cyan-500/10 to-blue-500/10',
      border: 'border-cyan-500/30',
      iconBg: 'bg-gradient-to-br from-cyan-500 to-blue-600',
      text: 'text-cyan-600'
    },
    { 
      gradient: 'from-emerald-500 to-teal-500',
      lightGradient: 'from-emerald-500/10 to-teal-500/10',
      border: 'border-emerald-500/30',
      iconBg: 'bg-gradient-to-br from-emerald-500 to-teal-600',
      text: 'text-emerald-600'
    },
    { 
      gradient: 'from-sky-500 to-cyan-500',
      lightGradient: 'from-sky-500/10 to-cyan-500/10',
      border: 'border-sky-500/30',
      iconBg: 'bg-gradient-to-br from-sky-500 to-cyan-600',
      text: 'text-sky-600'
    }
  ];

  useEffect(() => {
    const fetchSteps = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/how-it-works`);
        const data = await response.json();
        setHowItWorks(data);
      } catch (error) {
        console.error('Error fetching steps:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSteps();
  }, []);

  if (loading) {
    return (
      <section id="how-it-works" className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">Loading...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="how-it-works" className="py-20 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header - Enhanced */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wide">Simple Process</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            How It Works
          </h2>
          <p className="text-base sm:text-lg text-foreground/70 leading-relaxed">
            A simple, stress-free process designed around your pet's comfort and safety
          </p>
        </div>

        {/* Steps - Enhanced Visual Design */}
        <div className="max-w-6xl mx-auto">
          <div className="space-y-16">
            {howItWorks.map((step, index) => {
              const StepIcon = stepIcons[index % stepIcons.length];
              const theme = stepThemes[index % stepThemes.length];
              
              return (
                <div key={step.step} className="relative mb-16 last:mb-0">
                  <div className={`flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}>
                    {/* Content Card - Enhanced */}
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} group w-full`}>
                      <div className={`bg-white p-8 rounded-2xl shadow-lg border-2 ${theme.border} hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden`}>
                        {/* Gradient Background on Hover */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${theme.lightGradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                        
                        <div className="relative z-10">
                          {/* Step Badge with Icon */}
                          <div className={`flex items-center gap-3 mb-4 ${
                            index % 2 === 0 ? 'md:justify-end' : 'md:justify-start'
                          }`}>
                            <div className={`flex items-center gap-2 bg-gradient-to-r ${theme.gradient} px-4 py-2 rounded-full shadow-lg`}>
                              <StepIcon className="w-4 h-4 text-white" />
                              <span className="text-xs font-bold text-white uppercase tracking-wider">STEP {step.step}</span>
                            </div>
                          </div>
                          
                          {/* Title */}
                          <h3 className={`text-xl sm:text-2xl font-bold text-foreground mb-3 group-hover:${theme.text} transition-colors`}>
                            {step.title}
                          </h3>
                          
                          {/* Description */}
                          <p className="text-sm sm:text-base text-foreground/70 leading-relaxed group-hover:text-foreground/80 transition-colors">
                            {step.description}
                          </p>
                          
                          {/* Arrow indicator for flow */}
                          {index < howItWorks.length - 1 && (
                            <div className={`mt-6 inline-flex items-center gap-2 ${theme.text} text-sm font-semibold`}>
                              <span>Next Step</span>
                              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA - Enhanced */}
        <div className="mt-20 bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 md:p-12 text-center border-2 border-primary/20 shadow-xl relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-accent/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
          
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full mb-4">
              <MessageCircle className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-wide">Ready to Start?</span>
            </div>
            
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Questions About the Process?
            </h3>
            <p className="text-sm sm:text-base text-foreground/70 mb-8 max-w-2xl mx-auto leading-relaxed">
              Our experienced team is here to guide you through every step and answer all your questions
            </p>
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover:scale-105 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
            >
              Schedule a Consultation
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
